import { useEffect, useRef, useState } from 'react';
import { authStorageArea } from '../lib/supabase';

// Sign a user out after a stretch of no interaction.
//
// The app holds Article 9 health data on personal phones, and Supabase's own
// server-side inactivity timeout is a paid feature, so this is the control we
// can actually run today. Be honest about what it is: a client-side guard
// against someone picking up an unattended phone or clinic laptop. It is not a
// security boundary, because it cannot invalidate a refresh token that has
// already been extracted from the device. The server-side timeout on a Pro plan
// is the real fix and this stays useful alongside it, because it warns first.

export const IDLE_LIMIT_MS = 30 * 60 * 1000;   // sign out after this much quiet
export const IDLE_WARN_MS = 2 * 60 * 1000;     // warn this long before signing out
const LAST_ACTIVE_KEY = 'tbc-last-active';

// Interaction, not mere presence. Mouse movement alone would keep a session
// alive under a sleeping cat, so this wants deliberate input.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'submit'];

const now = () => Date.now();

// The marker goes wherever the session goes: sessionStorage normally, and
// localStorage once "Remember me" moves the session there. Keeping it in
// sessionStorage regardless was the hole — a remembered session outlived its
// own idle clock, so closing the app and opening it the next day walked
// straight back into the dashboard.
function readLastActive() {
  try {
    const v = Number(authStorageArea().getItem(LAST_ACTIVE_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeLastActive(t) {
  try { authStorageArea().setItem(LAST_ACTIVE_KEY, String(t)); } catch { /* private mode */ }
}

// Clears both stores, not just the current one: "Remember me" can have been
// turned off since the marker was written.
export function clearIdleMarker() {
  try { window.localStorage.removeItem(LAST_ACTIVE_KEY); } catch { /* private mode */ }
  try { window.sessionStorage.removeItem(LAST_ACTIVE_KEY); } catch { /* private mode */ }
}

// Has the stored marker already run past the limit? Asked at startup, before
// anything is rendered, so a session restored after a long gap goes to the
// login screen instead of painting the dashboard and yanking it away. No
// marker means no evidence either way — storage can be unavailable — and that
// must not sign anyone out.
export function idleExpired(idleMs = IDLE_LIMIT_MS) {
  const stored = readLastActive();
  return stored != null && now() - stored >= idleMs;
}

export function useIdleTimeout({
  enabled = true,
  onTimeout,
  idleMs = IDLE_LIMIT_MS,
  warnMs = IDLE_WARN_MS,
} = {}) {
  const [msLeft, setMsLeft] = useState(null); // non-null once the warning is up
  const lastActive = useRef(now());
  const firedRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) {
      setMsLeft(null);
      return undefined;
    }

    firedRef.current = false;

    // A phone that slept through the idle window never ran the timer, so the
    // stored stamp is what decides on wake. Same lesson as the midnight
    // rollover: wall-clock beats setInterval on a device that suspends.
    const stored = readLastActive();
    lastActive.current = stored ?? now();
    if (!stored) writeLastActive(lastActive.current);

    function markActive() {
      if (firedRef.current) return;
      lastActive.current = now();
      writeLastActive(lastActive.current);
      setMsLeft(null);
    }

    function check() {
      if (firedRef.current) return;
      const idleFor = now() - lastActive.current;

      if (idleFor >= idleMs) {
        firedRef.current = true;
        setMsLeft(null);
        clearIdleMarker();
        onTimeoutRef.current?.();
        return;
      }

      setMsLeft(idleFor >= idleMs - warnMs ? idleMs - idleFor : null);
    }

    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, markActive, { passive: true, capture: true }));

    function onWake() {
      if (document.visibilityState !== 'visible') return;
      // Re-read rather than trusting the ref: another tab may have been the
      // active one while this tab was hidden.
      lastActive.current = readLastActive() ?? lastActive.current;
      check();
    }
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    const timer = setInterval(check, 15 * 1000);
    check();

    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach(e =>
        window.removeEventListener(e, markActive, { capture: true }));
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [enabled, idleMs, warnMs]);

  function staySignedIn() {
    lastActive.current = now();
    writeLastActive(lastActive.current);
    setMsLeft(null);
  }

  return { msLeft, staySignedIn };
}
